import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { FaCamera, FaTag, FaCheckCircle, FaChalkboardTeacher, FaUser } from "react-icons/fa";
import { authFetch } from "../../utils/api/authFetch";
import { API_BASE_URL } from "../../config/apiConfig";
import { TagMultiSelect } from "../../components/marketplace";
import { CLASS_ORDER, sortClasses } from "../../utils/classOrder";
import { Panel } from "./shell";

// Resolve a stored profile_image (an absolute URL from uploads, a relative path,
// a data URI, or legacy base64) into something an <img src> can render.
const imgSrc = (v) => {
    if (!v) return null;
    if (/^https?:\/\//.test(v) || v.startsWith("data:")) return v;
    if (v.startsWith("/")) return `${API_BASE_URL}${v}`;
    return `data:image/jpeg;base64,${v}`;
};

const initials = (a, b) => `${(a || "")[0] || ""}${(b || "")[0] || ""}`.toUpperCase() || "T";

// Studio "Profile" tab — the teacher's public channel identity: photo, name,
// what they teach, and their monthly subscription price.
const StudioProfile = () => {
    const [loading, setLoading] = useState(true);
    const [me, setMe] = useState(null);

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [school, setSchool] = useState("");
    const [savingName, setSavingName] = useState(false);

    const [imgPreview, setImgPreview] = useState(null);
    const [uploadingImg, setUploadingImg] = useState(false);
    const fileRef = useRef(null);

    const [price, setPrice] = useState("");
    const [savedPrice, setSavedPrice] = useState(null);
    const [savingPrice, setSavingPrice] = useState(false);

    const [subjects, setSubjects] = useState([]);
    const [classes, setClasses] = useState([]);
    const [savingProfile, setSavingProfile] = useState(false);
    const [meta, setMeta] = useState({ classes: [], subjectsByClass: {} });

    // /api/quiz/metadata returns whatever classes the question bank happens to
    // hold, alphabetically — sort into school order, and fall back to the full
    // set so a thin bank never hides a class a teacher actually teaches.
    const classOptions = sortClasses(meta.classes.length ? meta.classes : CLASS_ORDER);
    const subjectOptions = useMemo(() => {
        const set = new Set();
        Object.values(meta.subjectsByClass || {}).forEach((arr) => (arr || []).forEach((s) => set.add(s)));
        return [...set].sort((a, b) => a.localeCompare(b));
    }, [meta]);

    const load = async () => {
        setLoading(true);
        try {
            const [uRes, pRes, mRes] = await Promise.all([
                authFetch("/api/users/me"),
                authFetch("/api/marketplace/plan"),
                authFetch("/api/quiz/metadata"),
            ]);
            const u = await uRes.json().catch(() => ({}));
            const p = await pRes.json().catch(() => ({}));
            const m = await mRes.json().catch(() => ({}));
            setMe(u);
            setFirstName(u.firstName || "");
            setLastName(u.lastName || "");
            setSchool(u.school || "");
            if (p.data?.price != null) { setPrice(String(p.data.price)); setSavedPrice(p.data.price); }
            setSubjects(p.data?.subjects || []);
            setClasses(sortClasses(p.data?.classes || []));
            setMeta({ classes: m.classes || [], subjectsByClass: m.subjectsByClass || {} });
        } catch (err) {
            toast.error("Could not load your profile");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const onPickImage = async (file) => {
        if (!file || !me?.user_id) return;
        setImgPreview(URL.createObjectURL(file));
        setUploadingImg(true);
        try {
            const fd = new FormData();
            fd.append("profile_image", file);
            const res = await authFetch(`/api/users/profile-image/${me.user_id}`, { method: "PATCH", body: fd });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || "Couldn't update your photo");
            setMe((m) => ({ ...m, profile_image: data.profile_image }));
            toast.success("Profile photo updated");
        } catch (err) {
            toast.error(err.message);
            setImgPreview(null);
        } finally {
            setUploadingImg(false);
        }
    };

    const saveName = async () => {
        if (!firstName.trim()) { toast.error("First name is required"); return; }
        setSavingName(true);
        try {
            const res = await authFetch(`/api/users/${me.user_id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ firstName: firstName.trim(), lastName: lastName.trim(), school: school.trim() }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || "Couldn't save your details");
            toast.success("Profile details saved");
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSavingName(false);
        }
    };

    const savePrice = async () => {
        const value = Math.round(Number(price));
        if (!value || value <= 0) { toast.error("Enter a valid monthly price"); return; }
        setSavingPrice(true);
        try {
            const res = await authFetch("/api/marketplace/plan", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ price: value }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || "Couldn't save your price");
            setSavedPrice(value);
            toast.success("Monthly price saved");
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSavingPrice(false);
        }
    };

    // "What you teach" saves on its own price-free endpoint — a teacher can set
    // their subjects/classes (which scope what their lessons can be tagged with)
    // without having to set a paid monthly price first.
    const saveProfile = async () => {
        setSavingProfile(true);
        try {
            const res = await authFetch("/api/marketplace/profile", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                // Persist in school order too, so every other surface that reads
                // the profile back (including the mobile app) gets it sorted.
                body: JSON.stringify({ subjects, classes: sortClasses(classes) }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || "Couldn't save what you teach");
            toast.success("Saved");
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSavingProfile(false);
        }
    };

    if (loading) return <p className="text-gray-500">Loading…</p>;
    const currentImg = imgPreview || imgSrc(me?.profile_image);

    return (
        <div className="space-y-6 max-w-2xl">
            {/* Photo + name */}
            <Panel title="Channel profile">
                <div className="flex flex-col sm:flex-row gap-5 items-start">
                    <div className="shrink-0">
                        <div className="relative w-24 h-24">
                            {currentImg ? (
                                <img src={currentImg} alt="profile" className="w-24 h-24 rounded-full object-cover bg-gray-100" />
                            ) : (
                                <div className="w-24 h-24 rounded-full bg-blue-600 text-white flex items-center justify-center text-2xl font-bold">
                                    {initials(firstName, lastName)}
                                </div>
                            )}
                            <button
                                onClick={() => fileRef.current?.click()}
                                disabled={uploadingImg}
                                className="absolute bottom-0 right-0 bg-white border border-gray-200 shadow rounded-full w-8 h-8 flex items-center justify-center text-gray-600 hover:text-blue-600 disabled:opacity-50"
                                title="Change photo"
                            >
                                <FaCamera size={13} />
                            </button>
                            <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png" className="hidden"
                                onChange={(e) => { onPickImage(e.target.files[0]); e.target.value = ""; }} />
                        </div>
                        {uploadingImg && <p className="text-[11px] text-gray-400 mt-2 text-center">Uploading…</p>}
                    </div>

                    <div className="flex-1 w-full space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <label className="text-sm text-gray-600">First name
                                <input className="border rounded-lg p-2 w-full mt-1" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                            </label>
                            <label className="text-sm text-gray-600">Last name
                                <input className="border rounded-lg p-2 w-full mt-1" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                            </label>
                        </div>
                        <label className="text-sm text-gray-600 block">School <span className="text-gray-400">(optional)</span>
                            <input className="border rounded-lg p-2 w-full mt-1" value={school} onChange={(e) => setSchool(e.target.value)} placeholder="e.g. Kings College" />
                        </label>
                        <button onClick={saveName} disabled={savingName} className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center gap-2">
                            <FaUser size={12} /> {savingName ? "Saving…" : "Save details"}
                        </button>
                    </div>
                </div>
            </Panel>

            {/* Monthly price */}
            <Panel title="Monthly price">
                <p className="text-xs text-gray-500 -mt-2 mb-3 flex items-center gap-1.5"><FaTag className="text-blue-600" /> Students pay this each month to unlock every lesson you list on the marketplace.</p>
                <div className="flex items-center gap-2 max-w-xs">
                    <span className="text-gray-500">₦</span>
                    <input type="number" min={1} value={price} onChange={(e) => setPrice(e.target.value)} placeholder="e.g. 2000" className="border rounded-lg p-2 w-full" />
                    <button onClick={savePrice} disabled={savingPrice} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 whitespace-nowrap">
                        {savingPrice ? "Saving…" : "Save"}
                    </button>
                </div>
                {savedPrice != null && (
                    <p className="text-xs text-green-600 mt-2 flex items-center gap-1"><FaCheckCircle /> Live at ₦{savedPrice.toLocaleString()}/month</p>
                )}
            </Panel>

            {/* What you teach */}
            <Panel title="What you teach">
                <p className="text-xs text-gray-500 -mt-2 mb-3 flex items-center gap-1.5"><FaChalkboardTeacher className="text-blue-600" /> Shown on your channel, and the list you tag your lessons from — only these subjects and classes appear when you create content. No price needed.</p>
                <div className="space-y-4">
                    <TagMultiSelect label="Subjects you teach" options={subjectOptions} value={subjects} onChange={setSubjects} placeholder="e.g. Physics" />
                    <TagMultiSelect label="Classes you teach" hint="Only the classes you pick here can be tagged on your lessons." options={classOptions} value={classes} onChange={(v) => setClasses(sortClasses(v))} allowCustom={false} placeholder="e.g. SS2" />
                    <button onClick={saveProfile} disabled={savingProfile} className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
                        {savingProfile ? "Saving…" : "Save what you teach"}
                    </button>
                </div>
            </Panel>
        </div>
    );
};

export default StudioProfile;
